// ============================================================
// TMR Portal Admin — Novo contrato: criar e, em seguida, enviar para assinatura
// ============================================================
import { useState } from 'react';
import type { MouseEvent } from 'react';
import { ApiError } from '@tmr/api-client';
import { Btn } from '../../components/primitives';
import { Field } from '../../components/ui';
import { Notice } from '../../components/Notice';
import { Icons, StrokeIcon } from '../../lib/icons';
import { useCreateContract } from '../../data/contracts/queries';
import { toCreateContractRequest } from '../../data/contracts/contract';
import type { ContractRecord } from '../../data/contracts/contract';
import { maskCNPJ } from '../../lib/format';
import { SendContractModal } from './SendContractModal';
import { OverlayPortal } from '../../components/OverlayPortal';

/** The Empresa a contract is drawn up for, as this modal needs to show and use it. */
export interface ContractCompany {
  id: string;
  name: string;
  cnpj: string;
  legacyProducerId: number | null;
  /**
   * The legal representative — the person who signs. Both contacts, because either can carry the document:
   * the phone by WhatsApp, the e-mail by e-mail. Having neither is what makes a contract unsendable.
   */
  representative: { name: string | null; email: string | null; phone: string | null };
  /**
   * Whether the Empresa may operate. A switched-off one is refused by the server, so the screens hide the
   * action rather than lead the operator to a 409.
   */
  active: boolean;
}

/** Props for {@link NewContractModal}. */
interface NewContractModalProps {
  company: ContractCompany;
  onClose: () => void;
  /**
   * Called when the modal closes having created a contract — whether or not the signature went out. The
   * caller uses it to open the new contract; it must not assume the contract was sent.
   */
  onCreated?: (contract: ContractRecord) => void;
}

/**
 * Draws up a contract and sends it for signature — two calls, two visible steps.
 *
 * They are deliberately not collapsed into one button. `POST /contracts` and `POST /contracts/{id}/signature`
 * are separate endpoints because the contract is meant to exist in tmr_v2 before an external provider is
 * involved, and the second call is the one that can fail for reasons outside our control. A single "criar e
 * enviar" would have to either report a failure while the contract exists, or hide a state the operator has
 * to manage. Chaining them server-side is a backend decision (03 Fase 5) that has not been built.
 *
 * So the modal never closes itself on a failure: everything after step one is recoverable, and closing is the
 * fastest way to lose track of a contract that was created. A contract left `Pending` keeps its "enviar"
 * action on the list and in the detail panel.
 *
 * **Step two is the shared confirmation.** It used to be a hand-rolled summary of five fields, two of which
 * the contract actually prints; now it is the same `SendContractModal` the list and the detail panel open,
 * over the real preview. One place decides what "conferir antes de enviar" means.
 */
export function NewContractModal({ company, onClose, onCreated }: NewContractModalProps) {
  const [contract, setContract] = useState<ContractRecord | null>(null);
  const create = useCreateContract();

  const stop = (e: MouseEvent) => e.stopPropagation();

  const submit = () => {
    create.mutate(toCreateContractRequest(company), {
      onSuccess: (created) => setContract(created),
    });
  };

  const finish = () => {
    if (contract) onCreated?.(contract);
    onClose();
  };

  // Step two: the contract exists, so the confirmation takes over. Closing it hands back to the caller,
  // which opens the contract — a send that failed leaves a Pending row with its action still there.
  if (contract) {
    return (
      <SendContractModal
        contractId={contract.id}
        eyebrow="Novo contrato · passo 2 de 2"
        onClose={finish}
        lead={
          <Notice tone="neutral">
            O contrato já foi criado, com a situação <strong>Pendente</strong>. Enviar gera o documento e o
            coloca na frente dos signatários.
          </Notice>
        }
      />
    );
  }

  return (
    <OverlayPortal>
      <div onClick={finish} style={overlayStyle}>
        <div onClick={stop} style={panelStyle}>
          <header style={headerStyle}>
            <div>
              <div className="pa-eyebrow" style={{ marginBottom: 4 }}>
                Novo contrato · passo 1 de 2
              </div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 19, margin: 0, letterSpacing: '-0.01em' }}>
                Criar o contrato
              </h3>
            </div>
            <button type="button" onClick={finish} className="pa-icon-btn" style={{ width: 34, height: 34 }}>
              <StrokeIcon d={Icons.x} size={18} />
            </button>
          </header>

          <div style={bodyStyle}>
            <Field label="Empresa">{company.name}</Field>
            <Field label="CNPJ">{maskCNPJ(company.cnpj)}</Field>
            <Field label="Assina pela empresa">
              {company.representative.name ?? <span style={{ color: 'var(--fg-subtle)' }}>—</span>}
            </Field>
            <Field label="E-mail do signatário">
              {company.representative.email ?? <span style={{ color: 'var(--fg-subtle)' }}>não cadastrado</span>}
            </Field>
            <Field label="Telefone do signatário">
              {company.representative.phone ?? <span style={{ color: 'var(--fg-subtle)' }}>não cadastrado</span>}
            </Field>
            <Field label="Assinatura via">ClickSign</Field>

            {company.active ? (
              <PreflightWarnings company={company} />
            ) : (
              <Notice tone="warning">
                Esta empresa está <strong>inativa</strong> e não emite contratos. Mude o status dela no cadastro
                para continuar.
              </Notice>
            )}

            {create.error && <FailureNotice error={create.error} />}
          </div>

          <footer style={footerStyle}>
            <span style={{ fontSize: 12, color: 'var(--fg-subtle)', maxWidth: 320 }}>
              Criar não envia nada para o provedor — o envio é o passo seguinte, com a conferência antes.
            </span>
            <div style={{ display: 'flex', gap: 10 }}>
              <Btn variant="secondary" onClick={finish}>
                Fechar
              </Btn>
              {/* The single door to creation, so the guard lives here rather than at each entry point. */}
              {company.active && (
                <Btn
                  variant="primary"
                  icon={create.isPending ? undefined : Icons.plus}
                  onClick={create.isPending ? undefined : submit}
                  disabled={create.isPending}
                >
                  {create.isPending ? 'Criando…' : 'Criar contrato'}
                </Btn>
              )}
            </div>
          </footer>
        </div>
      </div>
    </OverlayPortal>
  );
}

/**
 * What is known to go wrong before anything is attempted.
 *
 * Neither condition blocks the creation, on purpose: drawing up the contract now and fixing the record before
 * sending is a legitimate order of work, and the backend accepts it. Saying nothing would be the problem —
 * both faults only surface later, one as a 400 and the other as a repasse that never moves.
 */
function PreflightWarnings({ company }: { company: ContractCompany }) {
  const { email, phone } = company.representative;

  return (
    <>
      {/* Only when neither contact exists. A representative with just one of the two is perfectly sendable
          — by whichever channel that contact serves — and warning there would send someone to "fix" a
          cadastre that is not broken. The send screen is where the channel is picked and checked. */}
      {!email && !phone && (
        <Notice tone="warning">
          O representante legal não tem e-mail nem telefone cadastrado, e é por um dos dois que o provedor
          envia o documento. Criar o contrato agora é permitido, mas o <strong>envio vai falhar</strong> até
          o cadastro ser corrigido.
        </Notice>
      )}
      {!email && phone && (
        <Notice tone="neutral">
          O representante legal não tem e-mail cadastrado, então este contrato só pode sair{' '}
          <strong>por WhatsApp</strong>, para o telefone dele.
        </Notice>
      )}
      {company.legacyProducerId === null && (
        <Notice tone="neutral">
          Esta empresa ainda está sendo replicada, então o contrato nasce sem o vínculo que libera o repasse
          na assinatura. Ele é resolvido sozinho depois.
        </Notice>
      )}
    </>
  );
}

/**
 * A creation that failed, worded so the operator knows nothing was written.
 *
 * The backend's own `detail` is already operator-facing PT-BR and is shown as-is. The send's failures are no
 * longer handled here: they belong to the confirmation step, which is where the send now happens.
 */
function FailureNotice({ error }: { error: unknown }) {
  const apiError = error instanceof ApiError ? error : null;

  return (
    <Notice tone="danger">
      Não foi possível criar o contrato — nada foi gravado.
      {apiError && <div style={{ marginTop: 6 }}>{apiError.detail}</div>}
    </Notice>
  );
}

const overlayStyle = {
  position: 'fixed' as const,
  inset: 0,
  zIndex: 70,
  background: 'rgba(28,30,28,0.32)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
};

const panelStyle = {
  width: 560,
  maxWidth: '100%',
  maxHeight: '88vh',
  background: 'var(--cream-50)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-lg)',
  display: 'flex',
  flexDirection: 'column' as const,
  overflow: 'hidden',
};

const headerStyle = {
  padding: '20px 26px',
  borderBottom: '1px solid var(--border-default)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 16,
};

const bodyStyle = { flex: 1, overflowY: 'auto' as const, padding: '8px 26px 22px' };

const footerStyle = {
  padding: '16px 26px',
  borderTop: '1px solid var(--border-default)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 16,
};
